import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, Gift, Flame, Users, Trophy, Copy, Check, Star, Sparkles, Clock, Target, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsAgainGame = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [partnerEmail, setPartnerEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [completingMission, setCompletingMission] = useState(false);
  const [missionNote, setMissionNote] = useState('');
  const [showMissionComplete, setShowMissionComplete] = useState(false);
  const [surpriseResponse, setSurpriseResponse] = useState('');
  const [showSurprise, setShowSurprise] = useState(false);
  const [completionResult, setCompletionResult] = useState(null);

  useEffect(() => {
    loadGameData();
  }, []);

  const loadGameData = async () => {
    try {
      const response = await axios.get(`${API}/us-again/status`, { withCredentials: true });
      setGameData(response.data);
      if (response.data.surprise_drop) {
        setShowSurprise(true);
      }
    } catch (error) {
      console.error('Failed to load game data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async () => {
    if (!partnerEmail.trim()) {
      toast.error('Please enter your partner\'s email');
      return;
    }
    try {
      const response = await axios.post(`${API}/us-again/invite`, {
        partner_email: partnerEmail
      }, { withCredentials: true });
      setInviteCode(response.data.invite_code);
      toast.success('Invite created! Share the code with your partner.');
      loadGameData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    }
  };

  const handleJoinPartnership = async () => {
    if (!joinCode.trim()) {
      toast.error('Please enter the invite code');
      return;
    }
    try {
      await axios.post(`${API}/us-again/join`, {
        invite_code: joinCode.toUpperCase()
      }, { withCredentials: true });
      toast.success('Partnership activated! 💕');
      setShowJoin(false);
      loadGameData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join partnership');
    }
  };

  const handleCompleteMission = async () => {
    if (!gameData?.todays_mission) return;
    setCompletingMission(true);
    try {
      const response = await axios.post(`${API}/us-again/complete-mission`, {
        mission_id: gameData.todays_mission.id,
        note: missionNote
      }, { withCredentials: true });
      setCompletionResult(response.data);
      setShowMissionComplete(true);
      toast.success(`+${response.data.points_earned} points! 🎉`);
      loadGameData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete mission');
    } finally {
      setCompletingMission(false);
    }
  };

  const handleCompleteSurprise = async () => {
    if (!gameData?.surprise_drop) return;
    try {
      const response = await axios.post(`${API}/us-again/complete-surprise`, {
        surprise_id: gameData.surprise_drop.id,
        response: surpriseResponse
      }, { withCredentials: true });
      toast.success(`Surprise completed! +${response.data.points_earned} points! 🎁`);
      setShowSurprise(false);
      loadGameData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete surprise');
    }
  };

  const copyInviteCode = () => {
    // Fallback for environments where Clipboard API is blocked
    const textToCopy = inviteCode;
    
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        setCodeCopied(true);
        toast.success('Code copied!');
        setTimeout(() => setCodeCopied(false), 2000);
      }).catch(() => {
        fallbackCopy(textToCopy);
      });
    } else {
      fallbackCopy(textToCopy);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCodeCopied(true);
      toast.success('Code copied!');
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy. Please copy manually: ' + text);
    }
    
    document.body.removeChild(textArea);
  };

  const getCategoryColor = (category) => {
    const colors = {
      emotional: 'from-purple-500 to-purple-600',
      physical: 'from-pink-500 to-pink-600',
      mental: 'from-blue-500 to-blue-600',
      adventure: 'from-orange-500 to-orange-600',
      vulnerability: 'from-indigo-500 to-indigo-600'
    };
    return colors[category] || 'from-gray-500 to-gray-600';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      emotional: '💜',
      physical: '💗',
      mental: '🧠',
      adventure: '🌟',
      vulnerability: '🦋'
    };
    return icons[category] || '✨';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-pulse text-2xl text-purple-600">💕 Loading...</div>
      </div>
    );
  }

  // No Partnership - Show Setup
  if (!gameData?.has_partnership) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-indigo-100 pb-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white p-4 sticky top-0 z-50">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">US AGAIN</h1>
              <p className="text-sm opacity-90">Fall in love again. Together.</p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Hero Section */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-xl text-center">
            <div className="text-6xl mb-4">💞</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome to US AGAIN</h2>
            <p className="text-gray-600 mb-6">
              A game designed for couples to reconnect, grow stronger, and fall in love all over again.
            </p>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <div className="text-3xl mb-1">🔥</div>
                <p className="text-xs text-gray-500">Daily Missions</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-1">📊</div>
                <p className="text-xs text-gray-500">Connection Meter</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-1">🎁</div>
                <p className="text-xs text-gray-500">Surprise Drops</p>
              </div>
            </div>
          </div>

          {/* Invite Partner */}
          {!showInvite && !showJoin && (
            <div className="space-y-3">
              <Button 
                onClick={() => setShowInvite(true)}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-6 rounded-2xl text-lg font-semibold"
              >
                <Heart className="w-5 h-5 mr-2" />
                Invite Your Partner
              </Button>
              <Button 
                onClick={() => setShowJoin(true)}
                variant="outline"
                className="w-full border-2 border-purple-300 text-purple-600 py-6 rounded-2xl text-lg font-semibold"
              >
                <Users className="w-5 h-5 mr-2" />
                I Have an Invite Code
              </Button>
            </div>
          )}

          {/* Create Invite Form */}
          {showInvite && !inviteCode && (
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Invite Your Partner</h3>
              <input
                type="email"
                value={partnerEmail}
                onChange={(e) => setPartnerEmail(e.target.value)}
                placeholder="Partner's email"
                className="w-full p-3 border border-gray-300 rounded-xl mb-4 focus:ring-2 focus:ring-purple-500"
              />
              <div className="flex space-x-3">
                <Button onClick={() => setShowInvite(false)} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleCreateInvite} className="flex-1 bg-purple-500 hover:bg-purple-600">
                  Create Invite
                </Button>
              </div>
            </div>
          )}

          {/* Show Invite Code */}
          {inviteCode && (
            <div className="bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl p-6 text-white text-center">
              <Gift className="w-12 h-12 mx-auto mb-3" />
              <h3 className="text-lg font-bold mb-2">Share This Code</h3>
              <div className="bg-white/20 rounded-xl p-4 mb-4">
                <p className="text-3xl font-mono font-bold tracking-wider">{inviteCode}</p>
              </div>
              <Button onClick={copyInviteCode} variant="secondary" className="bg-white text-purple-600">
                {codeCopied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {codeCopied ? 'Copied!' : 'Copy Code'}
              </Button>
              <p className="text-sm mt-4 opacity-90">
                Send this code to your partner so they can join!
              </p>
            </div>
          )}

          {/* Join Form */}
          {showJoin && (
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Enter Invite Code</h3>
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX"
                maxLength={8}
                className="w-full p-4 text-center text-2xl font-mono border border-gray-300 rounded-xl mb-4 focus:ring-2 focus:ring-purple-500 tracking-widest"
              />
              <div className="flex space-x-3">
                <Button onClick={() => setShowJoin(false)} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleJoinPartnership} className="flex-1 bg-purple-500 hover:bg-purple-600">
                  Join Partnership
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Pending Partnership
  if (gameData?.partnership?.status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-indigo-100 pb-20">
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white p-4">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold">US AGAIN</h1>
          </div>
        </div>

        <div className="p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg text-center">
            <Clock className="w-16 h-16 mx-auto text-purple-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Waiting for Partner</h2>
            <p className="text-gray-600 mb-4">Share this code with your partner to start your journey together!</p>
            <div className="bg-purple-100 rounded-xl p-4 mb-4">
              <p className="text-3xl font-mono font-bold text-purple-600 tracking-wider">
                {gameData.partnership.invite_code}
              </p>
            </div>
            <Button onClick={() => {
              const code = gameData.partnership.invite_code;
              if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(code).then(() => {
                  toast.success('Code copied!');
                }).catch(() => {
                  // Fallback
                  const textArea = document.createElement('textarea');
                  textArea.value = code;
                  textArea.style.position = 'fixed';
                  textArea.style.left = '-999999px';
                  document.body.appendChild(textArea);
                  textArea.select();
                  try {
                    document.execCommand('copy');
                    toast.success('Code copied!');
                  } catch (err) {
                    toast.info(`Copy this code: ${code}`);
                  }
                  document.body.removeChild(textArea);
                });
              } else {
                toast.info(`Copy this code: ${code}`);
              }
            }} className="bg-purple-500 hover:bg-purple-600">
              <Copy className="w-4 h-4 mr-2" /> Copy Code
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active Partnership - Main Game
  const { partnership, partner, todays_mission, mission_completed_today, surprise_drop, badges } = gameData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-indigo-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">US AGAIN</h1>
              <p className="text-sm opacity-90">with {partner?.name || 'Partner'}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center space-x-1 text-yellow-300">
              <Flame className="w-5 h-5" />
              <span className="font-bold">{partnership.streak_count}</span>
            </div>
            <p className="text-xs opacity-80">day streak</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Connection Meter */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-700 flex items-center">
              <Heart className="w-5 h-5 mr-2 text-pink-500" />
              Connection Meter
            </h3>
            <span className="text-lg font-bold text-purple-600">{partnership.connection_meter}%</span>
          </div>
          <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 transition-all duration-500"
              style={{ width: `${partnership.connection_meter}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Complete missions together to strengthen your bond!
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 text-center shadow">
            <div className="text-2xl font-bold text-purple-600">{partnership.total_points}</div>
            <p className="text-xs text-gray-500">Points</p>
          </div>
          <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 text-center shadow">
            <div className="text-2xl font-bold text-pink-600">{partnership.missions_completed}</div>
            <p className="text-xs text-gray-500">Missions</p>
          </div>
          <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 text-center shadow">
            <div className="text-2xl font-bold text-indigo-600">{badges?.length || 0}</div>
            <p className="text-xs text-gray-500">Badges</p>
          </div>
        </div>

        {/* Today's Mission */}
        {!mission_completed_today && todays_mission ? (
          <div className={`bg-gradient-to-r ${getCategoryColor(todays_mission.category)} rounded-2xl p-5 shadow-lg text-white`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm bg-white/20 px-3 py-1 rounded-full">
                {getCategoryIcon(todays_mission.category)} Today's Mission
              </span>
              <span className="text-sm font-bold">+{todays_mission.points} pts</span>
            </div>
            <h3 className="text-xl font-bold mb-2">{todays_mission.title}</h3>
            <p className="text-white/90 mb-4">{todays_mission.description}</p>
            
            <textarea
              value={missionNote}
              onChange={(e) => setMissionNote(e.target.value)}
              placeholder="Add a note about how it went... (optional)"
              className="w-full p-3 rounded-xl bg-white/20 text-white placeholder-white/60 mb-3 border-0 focus:ring-2 focus:ring-white/50"
              rows={2}
            />
            
            <Button 
              onClick={handleCompleteMission}
              disabled={completingMission}
              className="w-full bg-white text-purple-600 hover:bg-white/90 font-bold py-3"
            >
              {completingMission ? (
                <span className="animate-pulse">Completing...</span>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Complete Mission Together
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl p-5 shadow-lg text-white text-center">
            <div className="text-4xl mb-2">✅</div>
            <h3 className="text-xl font-bold mb-1">Mission Complete!</h3>
            <p className="text-white/90">Come back tomorrow for a new mission</p>
            <div className="mt-3 flex items-center justify-center space-x-2">
              <Flame className="w-5 h-5 text-yellow-300" />
              <span className="font-bold">{partnership.streak_count} Day Streak!</span>
            </div>
          </div>
        )}

        {/* Surprise Drop Modal */}
        {showSurprise && surprise_drop && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full animate-bounce-in">
              <div className="text-center">
                <div className="text-5xl mb-3">🎁</div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">Surprise Drop!</h3>
                <span className="text-sm bg-purple-100 text-purple-600 px-3 py-1 rounded-full">
                  {surprise_drop.type} • +{surprise_drop.points} pts
                </span>
              </div>
              
              <div className="my-4 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
                <h4 className="font-bold text-gray-800 mb-2">{surprise_drop.title}</h4>
                <p className="text-gray-600">{surprise_drop.content}</p>
              </div>
              
              <textarea
                value={surpriseResponse}
                onChange={(e) => setSurpriseResponse(e.target.value)}
                placeholder="Your response... (optional)"
                className="w-full p-3 border border-gray-300 rounded-xl mb-3"
                rows={2}
              />
              
              <div className="flex space-x-3">
                <Button 
                  onClick={() => setShowSurprise(false)} 
                  variant="outline" 
                  className="flex-1"
                >
                  Later
                </Button>
                <Button 
                  onClick={handleCompleteSurprise} 
                  className="flex-1 bg-purple-500 hover:bg-purple-600"
                >
                  Complete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Badges Section */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
          <h3 className="font-semibold text-gray-700 flex items-center mb-3">
            <Trophy className="w-5 h-5 mr-2 text-yellow-500" />
            Badges Earned ({badges?.length || 0}/{gameData.all_badges?.length || 12})
          </h3>
          <div className="grid grid-cols-4 gap-2">
            {gameData.all_badges?.map((badge) => {
              const earned = badges?.some(b => b.id === badge.id);
              return (
                <div 
                  key={badge.id}
                  className={`text-center p-2 rounded-xl ${earned ? 'bg-gradient-to-br from-yellow-100 to-orange-100' : 'bg-gray-100 opacity-50'}`}
                >
                  <div className="text-2xl">{badge.icon}</div>
                  <p className="text-xs mt-1 text-gray-600 truncate">{badge.name}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Marriage Streak Display */}
        {partnership.streak_count >= 7 && (
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-4 shadow-lg text-white text-center">
            <Sparkles className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm opacity-90">We've chosen each other</p>
            <p className="text-3xl font-bold">{partnership.streak_count} days in a row</p>
            <p className="text-sm opacity-90 mt-1">💕 Keep the streak alive!</p>
          </div>
        )}
      </div>

      {/* Mission Complete Modal */}
      {showMissionComplete && completionResult && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full text-center">
            <div className="text-6xl mb-3">🎉</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Amazing!</h3>
            <p className="text-gray-600 mb-4">You completed today's mission together!</p>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-purple-100 rounded-xl p-3">
                <p className="text-2xl font-bold text-purple-600">+{completionResult.points_earned}</p>
                <p className="text-xs text-gray-500">Points</p>
              </div>
              <div className="bg-orange-100 rounded-xl p-3">
                <p className="text-2xl font-bold text-orange-600">{completionResult.streak_count}🔥</p>
                <p className="text-xs text-gray-500">Day Streak</p>
              </div>
            </div>
            
            {completionResult.new_badges?.length > 0 && (
              <div className="bg-yellow-100 rounded-xl p-3 mb-4">
                <p className="text-sm font-bold text-yellow-700 mb-1">New Badge Unlocked!</p>
                {completionResult.new_badges.map(b => (
                  <div key={b.id} className="text-2xl">{b.icon} {b.name}</div>
                ))}
              </div>
            )}
            
            <Button 
              onClick={() => setShowMissionComplete(false)}
              className="w-full bg-purple-500 hover:bg-purple-600"
            >
              Continue
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsAgainGame;
